//ROLES FOR THE APP DEFINED HERE
export const APP_ROLES = {
  SUPPORT_AGENT: 'SUPPORT_AGENT',
  COMPLIANCE: 'COMPLIANCE',
  OPERATIONS: 'OPERATIONS',
  FINANCE: 'FINANCE',
  MARKETING: 'MARKETING',
} as const;

export type AppRole = (typeof APP_ROLES)[keyof typeof APP_ROLES];

export const APP_PATHS = {
  DASHBOARD: '/dashboard',
  CLIENT_LOOKUP: '/client-lookup',
  CLIENT_CHANGES_LOG: '/client-lookup/changes-log',
  AUDIT_LOGS: '/audit-logs',
  TRANSACTIONS: '/transactions',
  TRANSACTIONS_DEPOSITS: '/transactions/deposits',
  TRANSACTIONS_WITHDRAWALS: '/transactions/withdrawals',
  TRANSACTIONS_AUDITS: '/transactions/audits',
  RECONCILIATION: '/reconciliation',
  BULK_SMS: '/bulk-sms',
  BULK_SMS_HISTORY: '/bulk-sms/history',
} as const;

export type AppPath = (typeof APP_PATHS)[keyof typeof APP_PATHS];
export const ACCESS_DENIED_PATH = '/access-denied';

const ALL_ACCESS = '*';

const OPERATIONS_ALLOWED_PATHS: AppPath[] = Object.values(APP_PATHS).filter(
  (p) => p !== APP_PATHS.RECONCILIATION
) as AppPath[];

const ROLE_ACCESS: Record<AppRole, Array<AppPath | typeof ALL_ACCESS>> = {
  [APP_ROLES.SUPPORT_AGENT]: [ALL_ACCESS],
  [APP_ROLES.COMPLIANCE]: [
    APP_PATHS.DASHBOARD,
    APP_PATHS.CLIENT_LOOKUP,
    APP_PATHS.CLIENT_CHANGES_LOG,
  ],
  // Operations can access every page except reconciliation.
  [APP_ROLES.OPERATIONS]: OPERATIONS_ALLOWED_PATHS,
  [APP_ROLES.FINANCE]: [
    APP_PATHS.DASHBOARD,
    APP_PATHS.RECONCILIATION,
  ],
  [APP_ROLES.MARKETING]: [
    APP_PATHS.DASHBOARD,
    APP_PATHS.BULK_SMS,
    APP_PATHS.BULK_SMS_HISTORY,
  ],
};

const HOME_FALLBACK_PATH = APP_PATHS.DASHBOARD;

function getKnownAppRoles(realmRoles: string[]): AppRole[] {
  const roleSet = new Set(realmRoles);
  return (Object.values(APP_ROLES) as AppRole[]).filter((role) => roleSet.has(role));
}

export function canAccessPath(realmRoles: string[], path: string): boolean {
  const knownRoles = getKnownAppRoles(realmRoles);
  if (knownRoles.length === 0) return false;
  return knownRoles.some((role) => {
    const allowed = ROLE_ACCESS[role];
    return allowed.includes(ALL_ACCESS) || allowed.includes(path as AppPath);
  });
}

export function getFirstAllowedPath(
  realmRoles: string[],
  preferredPaths: string[] = Object.values(APP_PATHS)
): string {
  const firstAllowed = preferredPaths.find((path) => canAccessPath(realmRoles, path));
  if (firstAllowed) return firstAllowed;
  return realmRoles.length > 0 ? ACCESS_DENIED_PATH : HOME_FALLBACK_PATH;
}
